import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useCallback, useMemo } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage } from "types/generated";

import {
  CONTEXTS_LIMIT,
  CONTEXTS_LIMIT_STORAGE_KEY,
  FILTER_TYPE,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
} from "./constants";
import { SEARCH_CONTEXTS } from "./gql";

const useContexts = () => {
  const [rowsPerPage] = useLocalStorage<number>(CONTEXTS_LIMIT_STORAGE_KEY);

  const { onError } = useTypedContext(FlashContext);

  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection: INITIAL_SORT_DIRECTION,
    initialSortOption: INITIAL_SORT_OPTION,
  });

  const initialVariables = useMemo(() => {
    return {
      input: {
        fullTextSearch: filterVariables.searchInput,
        predicates: filterVariables.predicates,
        ...(filterVariables.sortOptionFields && { orderBy: filterVariables.sortOptionFields }),
        first: rowsPerPage || CONTEXTS_LIMIT,
        after: null,
        requestedPage: null,
      },
    };
  }, [
    rowsPerPage,
    filterVariables.predicates,
    filterVariables.searchInput,
    filterVariables.sortOptionFields,
  ]);

  const skipQuery = !filterVariablesLoaded;

  const { error, loading, data, networkStatus, previousData, refetch } = usePolledQuery(
    SEARCH_CONTEXTS,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
      // APOLLO CLIENT UPDATE
      skip: skipQuery,
    }
  );

  const loadContexts = useCallback(
    async ({
      cursor,
      requestedPage,
      rowsPerPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      try {
        refetch({
          input: {
            ...initialVariables.input,
            after: cursor,
            first: rowsPerPage,
            requestedPage,
          },
        });
      } catch (error) {
        onError(error);
      }
    },
    [onError, initialVariables, refetch]
  );

  const initialLoading =
    (loading && networkStatus === NetworkStatus.loading && !data?.searchContexts) ||
    !filterVariablesLoaded;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const contexts = useMemo(
    () =>
      data?.searchContexts?.edges.map((edge) => edge.node) ||
      previousData?.searchContexts?.edges.map((edge) => edge.node) ||
      [],
    [data?.searchContexts?.edges, previousData?.searchContexts?.edges]
  );

  const paginationResetKey = useMemo(
    () => ({
      searchInput: filterVariables.searchInput,
      predicates: filterVariables.predicates,
      sortOptionFields: filterVariables.sortOptionFields,
    }),
    [filterVariables.searchInput, filterVariables.predicates, filterVariables.sortOptionFields]
  );

  return {
    loadContexts,
    error,
    contexts,
    initialLoading,
    noResults: !loading && !data?.searchContexts,
    endCursor: data?.searchContexts?.pageInfo.endCursor,
    startCursor: data?.searchContexts?.pageInfo.startCursor,
    isEmpty: data && !contexts.length,
    searchIsEmpty: data && !!(filterVariables.searchInput || filterVariables.predicates.length > 0),
    loadingContent,
    paginationResetKey,
  };
};

export default useContexts;
